import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';

const MIN_HEIGHT = 92;
const COMPACT_MIN_HEIGHT = 48;
type Options = {
	horizontalPadding: boolean;
	theme: ITheme;
	disabled: boolean;
	compact: boolean;
	hovered: boolean;
	checked: boolean;
	actionListBorder: string;
};

/**
 * A class with style.
 */
export default class ActionListItemStyle {
	/**
	 * Returns styles.
	 *
	 * @param options The props.
	 * @param options.theme Theme.
	 * @param options.disabled Disabled.
	 * @param options.compact For compact version.
	 * @param options.hovered Hovered.
	 * @param options.checked Checked.
	 * @param options.actionListBorder Border.
	 * @returns Styles.
	 */
	public static getStyle(options: Options): string {
		const { color, spacing, focusOutline, motion } = options.theme;
		return css`
			:host {
				display: block;
				pointer-events: ${options.disabled ? 'none' : 'initial'};
			}

			button {
				/* Disable user agent styling */
				background: none;
				outline: none;
				border: none;
				box-shadow: none;
				position: relative;
				text-align: initial;
				width: 100%;
				padding: ${options.compact ? spacing.space50 / 16 : spacing.space150 / 16}rem
					${this.getPadding(options) / 16}rem;
				min-height: ${options.compact ? COMPACT_MIN_HEIGHT / 16 : MIN_HEIGHT / 16}rem;
				display: flex;
				align-items: center;
				justify-content: space-between;
				color: ${options.disabled ? color.disabled.default : color.text.dark};
			}
			button::-moz-focus-inner {
				border: 0;
			}
			.outline {
				position: absolute;
				pointer-events: none;
				opacity: 0;
				transition-duration: ${focusOutline.transitionTime}s;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				transition-property: opacity, top, left, right, bottom;
				border-radius: ${focusOutline.borderRadius / 16}rem;
				box-shadow: 0 0 0 ${focusOutline.width / 16}rem;
			}
			button:focus-visible .outline {
				opacity: 1;
			}
			button:hover {
				cursor: ${options.disabled ? 'initial' : 'pointer'};
			}
			@media (pointer: fine) {
				button:hover .header {
					text-decoration: ${options.disabled ? 'inital' : 'underline'};
				}
			}
			.textContainer {
				display: flex;
				flex-direction: column;
			}
			.spacing {
				width: ${spacing.space125 / 16}rem;
				flex-shrink: 0;
			}
			.icon {
				transition: transform ${motion.duration.medium} ${motion.ease.easy};
				transition-duration: ${motion.duration.medium};
				color: ${options.disabled ? color.disabled.default : color.icon.default};
			}
			img {
				width: ${48 / 16}rem;
				height: ${48 / 16}rem;
				opacity: ${options.disabled ? 0.5 : 1};
			}
			.container {
				display: flex;
				align-items: center;
				margin-inline-end: ${spacing.space125 / 16}rem;
			}
			${this.getBorder(options)}
			${this.getRtlRules()}
			${this.getHoveredStyle(options)}
		`;
	}

	/**
	 * Returns styles.
	 *
	 * @param options The props.
	 * @param options.theme Theme.
	 * @returns Styles.
	 */
	private static getPadding(options: Options): number {
		if (!options.horizontalPadding) {
			return 0;
		}
		return options.theme.spacing.space150;
	}

	/**
	 * Returns styles.
	 *
	 * @returns Styles.
	 */
	private static getRtlRules(): string {
		return css`
			:host(:dir(rtl)) .icon {
				transform: rotate(-180deg);
			}
			:host-context([dir='rtl']) .icon {
				transform: rotate(-180deg);
				margin-right: auto;
				margin-left: 0;
			}
		`;
	}

	private static getHoveredStyle(options: Options): string {
		if (options.hovered && !options.checked) {
			return css`
				@media (pointer: fine) {
					button .header {
						text-decoration: ${options.disabled ? 'inital' : 'underline'};
					}
				}
			`;
		}
		return '';
	}

	/**
	 * Returns styles.
	 *
	 * @param options The props.
	 * @param options.theme Theme.
	 * @returns Styles.
	 */
	private static getBorder(options: Options): string {
		if (options.actionListBorder !== 'none') {
			return css`
				:host {
					/* stylelint-disable-next-line */
					border-${options.actionListBorder}: ${options.theme.border.thickness.thin / 16}rem solid
					${options.disabled ? options.theme.color.disabled.default : options.theme.color.border.light};
				}
			`;
		}

		return '';
	}
}
